import { useCallback, useRef, useState } from 'react';
import { MAP_VIEW_BOX } from './map-scale';

/** The continent always fits at scale 1; past this a country fills the frame. */
const MAX_SCALE = 8;
const MIN_SCALE = 1;
const ZOOM_STEP = 1.6;
/** Scale a single country is read at when the reader picks it. */
const COUNTRY_SCALE = 3.4;

export interface MapViewport {
  scale: number;
  /** Translation in view-box units, applied before the scale. */
  x: number;
  y: number;
}

const IDENTITY: MapViewport = { scale: 1, x: 0, y: 0 };

/** Keeps the drawn continent inside the frame, whatever the reader does. */
function clamp(viewport: MapViewport): MapViewport {
  const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, viewport.scale));
  const slackX = (MAP_VIEW_BOX.width * (scale - 1)) / (2 * scale);
  const slackY = (MAP_VIEW_BOX.height * (scale - 1)) / (2 * scale);
  return {
    scale,
    x: Math.min(slackX, Math.max(-slackX, viewport.x)),
    y: Math.min(slackY, Math.max(-slackY, viewport.y)),
  };
}

/**
 * Zoom, pan and focus for the map, in view-box units. The transform lives on a
 * group so the browser animates it, and the geometry is never recomputed.
 */
export function useMapViewport() {
  const [viewport, setViewport] = useState<MapViewport>(IDENTITY);
  const [isPanning, setIsPanning] = useState(false);
  const dragOrigin = useRef<{ pointerX: number; pointerY: number; viewport: MapViewport } | null>(null);

  const zoomBy = useCallback((factor: number) => {
    setViewport((current) => clamp({ ...current, scale: current.scale * factor }));
  }, []);

  const reset = useCallback(() => setViewport(IDENTITY), []);

  /** Brings one point of the map, in view-box units, to the centre of the frame. */
  const focusOn = useCallback((x: number, y: number) => {
    setViewport((current) => {
      const scale = current.scale >= COUNTRY_SCALE ? current.scale : COUNTRY_SCALE;
      const centreX = MAP_VIEW_BOX.x + MAP_VIEW_BOX.width / 2;
      const centreY = MAP_VIEW_BOX.y + MAP_VIEW_BOX.height / 2;
      return clamp({ scale, x: centreX - x, y: centreY - y });
    });
  }, []);

  /**
   * Frames a set of points, which is how the reader asks for the countries that
   * actually carry contributions. The continent stays the default view: the
   * frame only moves when someone asks for it.
   */
  const fitTo = useCallback((points: readonly { x: number; y: number }[]) => {
    if (points.length === 0) return;
    const xs = points.map((point) => point.x);
    const ys = points.map((point) => point.y);
    const width = Math.max(...xs) - Math.min(...xs);
    const height = Math.max(...ys) - Math.min(...ys);
    const padding = 1.5;
    const scale = Math.min(
      MAX_SCALE,
      Math.max(
        MIN_SCALE,
        Math.min(
          MAP_VIEW_BOX.width / Math.max(width * padding, 1),
          MAP_VIEW_BOX.height / Math.max(height * padding, 1),
        ),
      ),
    );
    const centreX = MAP_VIEW_BOX.x + MAP_VIEW_BOX.width / 2;
    const centreY = MAP_VIEW_BOX.y + MAP_VIEW_BOX.height / 2;
    setViewport(
      clamp({
        scale,
        x: centreX - (Math.min(...xs) + width / 2),
        y: centreY - (Math.min(...ys) + height / 2),
      }),
    );
  }, []);

  const onPointerDown = useCallback(
    (event: React.PointerEvent<SVGSVGElement>) => {
      if (event.button !== 0) return;
      event.currentTarget.setPointerCapture(event.pointerId);
      dragOrigin.current = { pointerX: event.clientX, pointerY: event.clientY, viewport };
      setIsPanning(true);
    },
    [viewport],
  );

  const onPointerMove = useCallback((event: React.PointerEvent<SVGSVGElement>) => {
    const origin = dragOrigin.current;
    if (!origin) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const unitsPerPixelX = MAP_VIEW_BOX.width / rect.width;
    const unitsPerPixelY = MAP_VIEW_BOX.height / rect.height;
    setViewport(
      clamp({
        scale: origin.viewport.scale,
        x: origin.viewport.x + ((event.clientX - origin.pointerX) * unitsPerPixelX) / origin.viewport.scale,
        y: origin.viewport.y + ((event.clientY - origin.pointerY) * unitsPerPixelY) / origin.viewport.scale,
      }),
    );
  }, []);

  const endPan = useCallback(() => {
    dragOrigin.current = null;
    setIsPanning(false);
  }, []);

  const onWheel = useCallback(
    (event: React.WheelEvent<SVGSVGElement>) => {
      if (!event.ctrlKey && Math.abs(event.deltaY) < 1) return;
      event.preventDefault();
      zoomBy(event.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP);
    },
    [zoomBy],
  );

  return {
    viewport,
    isPanning,
    isZoomed: viewport.scale > MIN_SCALE,
    zoomIn: () => zoomBy(ZOOM_STEP),
    zoomOut: () => zoomBy(1 / ZOOM_STEP),
    reset,
    focusOn,
    fitTo,
    handlers: { onPointerDown, onPointerMove, onPointerUp: endPan, onPointerCancel: endPan, onWheel },
  };
}
